import React, { useContext, useEffect, useState, useCallback } from "react";
import { ApiContext } from "../../../../context/MyContext";
import { useUtils } from "../../../../context/UtilsContext";
import {
  fetchClientSubscriptionsByClient,
  fetchEmployeesForSubscription,
  modifyClientSubscriptions,
  modifyClientSubscriptionCoaches,
  modifyClientSubscriptionExtensions,
} from "../../../../service/SubscriptionWellDhanService";
import { fetchUserPlans } from "../../../../service/pricingPlanService";
import Loader from "../../../../common/Loader/Loader";
import Button from "../../../../common/Button/Button";
import { Plus, Trash2, CreditCard, Users, CalendarClock, Pencil, Calendar, IndianRupee, Banknote } from "lucide-react";

// Display format for cards, hints, etc. (date only, no timezone shift).
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const dateOnly = String(iso).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return iso;
    const [y, m, d] = dateOnly.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

// dd/mm/yyyy for date inputs only: format yyyy-mm-dd -> dd/mm/yyyy
const formatDateForInput = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return "";
  const s = String(yyyyMmDd).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return yyyyMmDd;
  const [y, m, d] = s.split("-");
  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
};
// Parse dd/mm/yyyy or d/m/yyyy -> yyyy-mm-dd; returns "" if invalid
const parseDateFromInput = (str) => {
  if (!str || typeof str !== "string") return "";
  const trimmed = str.trim().replace(/\s/g, "");
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return "";
  const [, d, m, y] = match;
  const day = parseInt(d, 10);
  const month = parseInt(m, 10);
  const year = parseInt(y, 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const isCompleteYyyyMmDd = (value) => {
  if (!value || typeof value !== "string") return false;
  const s = value.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

// Date input that displays and accepts dd/mm/yyyy; stores yyyy-mm-dd in state
function DateInput({ value, onChange, className, required, id }) {
  const [displayVal, setDisplayVal] = React.useState(() => formatDateForInput(value));
  React.useEffect(() => {
    setDisplayVal(formatDateForInput(value));
  }, [value]);
  const handleChange = (e) => {
    const v = e.target.value;
    setDisplayVal(v);
    const parsed = parseDateFromInput(v);
    if (parsed) onChange(parsed);
  };
  const handleBlur = () => {
    const parsed = parseDateFromInput(displayVal);
    if (parsed) onChange(parsed);
    else if (!displayVal.trim()) onChange("");
    else setDisplayVal(formatDateForInput(value));
  };
  return (
    <input
      type="text"
      id={id}
      value={displayVal}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="dd/mm/yyyy"
      className={className}
      required={required}
      autoComplete="off"
    />
  );
}

function Modal({ title, onClose, children, maxWidthClass = "max-w-md" }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`bg-white rounded-xl shadow-xl border border-gray-200 w-full ${maxWidthClass} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function Subscriptions({ profileId, showAlert }) {
  const { get } = useContext(ApiContext);
  const { sessionUtils } = useUtils();
  // Event type for session logging: must match current app (e.g. flexboard) so user-profile accepts it
  const eventTypeId =
    sessionUtils?.event_types?.[0]?.event_types?.find((e) => e.description === "modify_user_profile")?.id ?? null;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [addSubscriptionOpen, setAddSubscriptionOpen] = useState(false);
  const [editSubscriptionId, setEditSubscriptionId] = useState(null);
  const [modifyCoachSubscriptionId, setModifyCoachSubscriptionId] = useState(null);
  const [editCoachId, setEditCoachId] = useState(null);
  const [addExtensionSubscriptionId, setAddExtensionSubscriptionId] = useState(null);
  const [editExtensionId, setEditExtensionId] = useState(null);
  const [formSub, setFormSub] = useState({
    plan_pricing_id: "",
    selected_plan_id: "",
    selected_country: "",
    selected_currency: "",
    amount: "",
    active_from: "",
    payment_date: "",
    sales_rep_employee_id: "",
  });
  const [formCoach, setFormCoach] = useState({ coach_type: "fitness", coach_id: "", active_from: "" });
  const [formExt, setFormExt] = useState({
    extension_type: "pause",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [planCatalogQuery, setPlanCatalogQuery] = useState("");

  const showAlertRef = React.useRef(showAlert);
  showAlertRef.current = showAlert;

  const loadData = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const [subRes, plansData, empRes] = await Promise.all([
        fetchClientSubscriptionsByClient(profileId),
        eventTypeId ? fetchUserPlans(get, eventTypeId).catch(() => []) : Promise.resolve([]),
        fetchEmployeesForSubscription().catch(() => ({ success: false, data: [] })),
      ]);
      // Support both res.data.data (nested) and res.data (direct) from fetch API
      const payload = subRes?.data?.data ?? subRes?.data;
      if (subRes.success && payload) {
        setSubscriptions(Array.isArray(payload.subscriptions) ? payload.subscriptions : []);
        setCoaches(Array.isArray(payload.subscription_coaches) ? payload.subscription_coaches : []);
        setExtensions(Array.isArray(payload.subscription_extensions) ? payload.subscription_extensions : []);
      } else {
        setSubscriptions([]);
        setCoaches([]);
        setExtensions([]);
        if (!subRes.success && subRes.message) {
          showAlertRef.current?.(subRes.message);
        }
      }
      setPlans(Array.isArray(plansData) ? plansData : []);
      setEmployees(Array.isArray(empRes?.data) ? empRes.data : []);
    } catch (e) {
      setSubscriptions([]);
      setCoaches([]);
      setExtensions([]);
      showAlertRef.current?.(e?.message || "Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  }, [profileId, get, eventTypeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Local date as YYYY-MM-DD (avoid UTC so "today" matches user's date)
  const today = useCallback(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  // Default date within [subStart, subEnd]: today if in range, else subStart
  const defaultDateInRange = useCallback((subStart, subEnd) => {
    if (!subStart || !subEnd) return today();
    const s = String(subStart).slice(0, 10);
    const e = String(subEnd).slice(0, 10);
    const t = today();
    if (t >= s && t <= e) return t;
    return s;
  }, [today]);

  useEffect(() => {
    if (addSubscriptionOpen) {
      setFormSub((prev) => ({
        ...prev,
        active_from: prev.active_from || today(),
        payment_date: prev.payment_date || today(),
      }));
    }
  }, [addSubscriptionOpen, today]);

  useEffect(() => {
    if (!modifyCoachSubscriptionId) return;
    const sub = subscriptions.find((s) => s.id === modifyCoachSubscriptionId);
    const subStart = sub?.active_from ? String(sub.active_from).slice(0, 10) : null;
    const subEnd = sub?.active_to ? String(sub.active_to).slice(0, 10) : null;
    const defaultFrom = subStart && subEnd ? defaultDateInRange(subStart, subEnd) : today();
    setFormCoach((prev) => ({ ...prev, active_from: prev.active_from || defaultFrom }));
  }, [modifyCoachSubscriptionId, subscriptions, today, defaultDateInRange]);

  useEffect(() => {
    if (!addExtensionSubscriptionId) return;
    const sub = subscriptions.find((s) => s.id === addExtensionSubscriptionId);
    const subStart = sub?.active_from ? String(sub.active_from).slice(0, 10) : null;
    const subEnd = sub?.active_to ? String(sub.active_to).slice(0, 10) : null;
    const defaultStart = subStart && subEnd ? defaultDateInRange(subStart, subEnd) : today();
    setFormExt((prev) => ({
      ...prev,
      start_date: prev.start_date || defaultStart,
      end_date: prev.end_date || defaultStart,
    }));
  }, [addExtensionSubscriptionId, subscriptions, today, defaultDateInRange]);

  const flatPricings = plans.flatMap((p) =>
    (p.pricing || []).map((pr) => ({
      id: pr.planPricingId,
      planId: p.planId,
      planName: p.planName,
      country: pr.country || "",
      currency: pr.currency || "",
      durationMonths: pr.duration != null ? Number(pr.duration) : null,
      label: `${p.planName} – ${pr.country || "N/A"} / ${pr.currency || "N/A"}`,
      price: pr.price != null ? Number(pr.price) : null,
      durationDays: pr.planDurationDays ?? (pr.duration != null ? Math.round(Number(pr.duration) * 30) : 0),
    }))
  );

  const addDays = (dateStr, days) => {
    if (!dateStr || !days) return null;
    const d = new Date(dateStr);
    d.setDate(d.getDate() + Number(days));
    return d.toISOString().slice(0, 10);
  };

  // End date is inclusive (last day of subscription). No overlap when new starts after existing end.
  const rangesOverlap = (start1, end1, start2, end2) => {
    if (!start1 || !start2) return false;
    const e1 = end1 || "9999-12-31";
    const e2 = end2 || "9999-12-31";
    const s1 = String(start1).slice(0, 10);
    const s2 = String(start2).slice(0, 10);
    const end1Str = String(e1).slice(0, 10);
    const end2Str = String(e2).slice(0, 10);
    return s1 <= end2Str && end1Str > s2;
  };
  const pricingById = Object.fromEntries(flatPricings.map((pr) => [pr.id, pr]));
  const pricingLabelById = Object.fromEntries(flatPricings.map((pr) => [pr.id, pr.label]));
  const subscriptionLabelById = Object.fromEntries(
    subscriptions.map((s) => [
      s.id,
      pricingLabelById[s.plan_pricing_id] || `${s.plan_pricing_id?.slice(0, 8) || "—"}…`,
    ])
  );

  const employeeIdToName = React.useMemo(() => {
    const map = {};
    (employees || []).forEach((emp) => {
      const name = [emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.employee_id || "—";
      map[emp.employee_id] = name;
    });
    return map;
  }, [employees]);

  const coachesBySubId = React.useMemo(() => {
    const map = {};
    coaches.forEach((c) => {
      const sid = c.client_subscription_id;
      if (!map[sid]) map[sid] = [];
      map[sid].push(c);
    });
    return map;
  }, [coaches]);

  const extensionsBySubId = React.useMemo(() => {
    const map = {};
    extensions.forEach((ex) => {
      const sid = ex.client_subscription_id;
      if (!map[sid]) map[sid] = [];
      map[sid].push(ex);
    });
    return map;
  }, [extensions]);

  const handleAddSubscription = async (e) => {
    e.preventDefault();
    const amount = formSub.amount !== "" && formSub.amount != null ? parseFloat(formSub.amount) : null;
    if (!formSub.plan_pricing_id || !formSub.active_from) {
      showAlert?.("Please select plan pricing details and enter the start date.");
      return;
    }
    if (amount == null || isNaN(amount) || amount < 0) {
      showAlert?.("Please enter a valid amount.");
      return;
    }
    if (!isCompleteYyyyMmDd(formSub.payment_date)) {
      showAlert?.("Please enter a valid payment date.");
      return;
    }
    const durationDays = pricingById[formSub.plan_pricing_id]?.durationDays ?? 0;
    const newActiveTo = addDays(formSub.active_from, durationDays);
    const overlaps = subscriptions.some((sub) =>
      rangesOverlap(formSub.active_from, newActiveTo, sub.active_from, sub.active_to)
    );
    if (overlaps) {
      showAlert?.("This client already has a subscription in this date range. Choose different dates or end the existing one first.");
      return;
    }
    setSaving(true);
    try {
      const salesRepId = formSub.sales_rep_employee_id?.trim() || null;
      const paymentDateVal = formSub.payment_date.trim().slice(0, 10);
      const res = await modifyClientSubscriptions({
        event_type_id: eventTypeId,
        subscriptions: [
          {
            operation: "ADD",
            id: null,
            plan_pricing_id: formSub.plan_pricing_id,
            client_id: profileId,
            amount,
            sales_rep_employee_id: salesRepId,
            active_from: formSub.active_from,
            payment_date: paymentDateVal,
          },
        ],
      });
      if (res.success) {
        setAddSubscriptionOpen(false);
        setFormSub({
          plan_pricing_id: "",
          selected_plan_id: "",
          selected_country: "",
          selected_currency: "",
          amount: "",
          active_from: "",
          payment_date: "",
          sales_rep_employee_id: "",
        });
        // Optimistic update: merge created subscription(s) from API response so UI updates immediately
        const created = Array.isArray(res.data?.data) ? res.data.data : [];
        if (created.length > 0) {
          setSubscriptions((prev) => [...created, ...prev]);
        }
        showAlert?.("Subscription added");
        // Refetch to get full list (and coaches/extensions) in sync with server
        try {
          await loadData();
        } catch (refetchErr) {
          // Already updated optimistically; just log or notify if refetch failed
          console.warn("Refetch after add failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to add subscription");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to add subscription");
    } finally {
      setSaving(false);
    }
  };

  const openEditSubscription = (sub) => {
    const fromDate = sub.active_from ? String(sub.active_from).slice(0, 10) : today();
    const pricingMeta = pricingById[sub.plan_pricing_id] || {};
    setFormSub({
      plan_pricing_id: sub.plan_pricing_id || "",
      selected_plan_id: pricingMeta.planId ? String(pricingMeta.planId) : "",
      selected_country: pricingMeta.country || "",
      selected_currency: pricingMeta.currency || "",
      amount: sub.amount != null ? String(sub.amount) : "",
      active_from: fromDate,
      payment_date: sub.payment_date ? String(sub.payment_date).slice(0, 10) : "",
      sales_rep_employee_id: sub.sales_rep_employee_id || "",
    });
    setEditSubscriptionId(sub.id);
  };

  const handleEditSubscription = async (e) => {
    e.preventDefault();
    if (!editSubscriptionId) return;
    const amount = formSub.amount !== "" && formSub.amount != null ? parseFloat(formSub.amount) : null;
    if (!formSub.plan_pricing_id || !formSub.active_from) {
      showAlert?.("Please select plan pricing details and enter the start date.");
      return;
    }
    if (amount == null || isNaN(amount) || amount < 0) {
      showAlert?.("Please enter a valid amount.");
      return;
    }
    if (!isCompleteYyyyMmDd(formSub.payment_date)) {
      showAlert?.("Please enter a valid payment date.");
      return;
    }
    const durationDays = pricingById[formSub.plan_pricing_id]?.durationDays ?? 0;
    const newActiveTo = addDays(formSub.active_from, durationDays);
    const overlaps = subscriptions.some((sub) => {
      if (sub.id === editSubscriptionId) return false;
      return rangesOverlap(formSub.active_from, newActiveTo, sub.active_from, sub.active_to);
    });
    if (overlaps) {
      showAlert?.("This client already has another subscription in this date range. Choose different dates.");
      return;
    }
    setSaving(true);
    try {
      const salesRepId = formSub.sales_rep_employee_id?.trim() || null;
      const paymentDateVal = formSub.payment_date.trim().slice(0, 10);
      const res = await modifyClientSubscriptions({
        event_type_id: eventTypeId,
        subscriptions: [
          {
            operation: "UPDATE",
            id: editSubscriptionId,
            plan_pricing_id: formSub.plan_pricing_id,
            client_id: profileId,
            amount,
            sales_rep_employee_id: salesRepId,
            active_from: formSub.active_from,
            payment_date: paymentDateVal,
          },
        ],
      });
      if (res.success) {
        setEditSubscriptionId(null);
        setFormSub({
          plan_pricing_id: "",
          selected_plan_id: "",
          selected_country: "",
          selected_currency: "",
          amount: "",
          active_from: "",
          payment_date: "",
          sales_rep_employee_id: "",
        });
        showAlert?.("Subscription updated");
        try {
          await loadData();
        } catch (refetchErr) {
          console.warn("Refetch after edit failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to update subscription");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to update subscription");
    } finally {
      setSaving(false);
    }
  };

  const handleAddCoach = async (e) => {
    e.preventDefault();
    const subId = modifyCoachSubscriptionId || formCoach.client_subscription_id;
    const activeFrom = formCoach.active_from?.trim() || today();
    if (!subId || !formCoach.coach_type || !formCoach.coach_id?.trim()) {
      showAlert?.("Please select coach type and coach.");
      return;
    }
    // At most one fitness and one diet coach per subscription
    const subCoaches = coachesBySubId[subId] || [];
    const typeLower = (formCoach.coach_type || "").toLowerCase();
    if (typeLower === "fitness" || typeLower === "diet") {
      const hasSameType = subCoaches.some((c) => (c.coach_type || "").toLowerCase() === typeLower);
      if (hasSameType) {
        showAlert?.(`This subscription already has a ${typeLower} coach. Remove or edit the existing one.`);
        return;
      }
    }
    // Coach active from must be within the subscription period
    const sub = subscriptions.find((s) => s.id === subId);
    if (sub?.active_from != null && sub?.active_to != null) {
      const subStart = String(sub.active_from).slice(0, 10);
      const subEnd = String(sub.active_to).slice(0, 10);
      if (activeFrom < subStart || activeFrom > subEnd) {
        showAlert?.("Coach active from date must be within the subscription period (from plan start to plan end).");
        return;
      }
    }
    setSaving(true);
    try {
      const res = await modifyClientSubscriptionCoaches({
        event_type_id: eventTypeId,
        subscription_coaches: [
          {
            operation: "ADD",
            id: null,
            client_subscription_id: subId,
            coach_type: formCoach.coach_type,
            coach_id: formCoach.coach_id.trim(),
            active_from: activeFrom,
          },
        ],
      });
      if (res.success) {
        setModifyCoachSubscriptionId(null);
        setFormCoach({ coach_type: "fitness", coach_id: "", active_from: "" });
        showAlert?.("Coach assigned");
        try {
          await loadData();
        } catch (refetchErr) {
          console.warn("Refetch after assign coach failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to assign coach");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to assign coach");
    } finally {
      setSaving(false);
    }
  };

  const handleEditCoach = async (e) => {
    e.preventDefault();
    if (!editCoachId) return;
    const subId = modifyCoachSubscriptionId;
    const activeFrom = formCoach.active_from?.trim() || today();
    if (!subId || !formCoach.coach_type || !formCoach.coach_id?.trim()) {
      showAlert?.("Please select coach type and coach.");
      return;
    }
    // At most one fitness and one diet coach per subscription (other than the one we're editing)
    const subCoaches = coachesBySubId[subId] || [];
    const typeLower = (formCoach.coach_type || "").toLowerCase();
    if (typeLower === "fitness" || typeLower === "diet") {
      const otherWithSameType = subCoaches.some(
        (c) => c.id !== editCoachId && (c.coach_type || "").toLowerCase() === typeLower
      );
      if (otherWithSameType) {
        showAlert?.(`This subscription already has a ${typeLower} coach. Remove or edit the existing one.`);
        return;
      }
    }
    const sub = subscriptions.find((s) => s.id === subId);
    if (sub?.active_from != null && sub?.active_to != null) {
      const subStart = String(sub.active_from).slice(0, 10);
      const subEnd = String(sub.active_to).slice(0, 10);
      if (activeFrom < subStart || activeFrom > subEnd) {
        showAlert?.("Coach active from date must be within the subscription period (from plan start to plan end).");
        return;
      }
    }
    setSaving(true);
    try {
      const res = await modifyClientSubscriptionCoaches({
        event_type_id: eventTypeId,
        subscription_coaches: [
          {
            operation: "UPDATE",
            id: editCoachId,
            client_subscription_id: subId,
            coach_type: formCoach.coach_type,
            coach_id: formCoach.coach_id.trim(),
            active_from: activeFrom,
          },
        ],
      });
      if (res.success) {
        setEditCoachId(null);
        setFormCoach({ coach_type: "fitness", coach_id: "", active_from: "" });
        showAlert?.("Coach updated");
        try {
          await loadData();
        } catch (refetchErr) {
          console.warn("Refetch after edit coach failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to update coach");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to update coach");
    } finally {
      setSaving(false);
    }
  };

  const handleAddExtension = async (e) => {
    e.preventDefault();
    const subId = addExtensionSubscriptionId || formExt.client_subscription_id;
    const reason = formExt.reason?.trim() || null;
    if (!subId || !formExt.start_date || !formExt.end_date) {
      showAlert?.("Please enter start and end dates.");
      return;
    }
    if (!reason) {
      showAlert?.("Please enter a reason.");
      return;
    }
    const startStr = String(formExt.start_date).slice(0, 10);
    const endStr = String(formExt.end_date).slice(0, 10);
    if (startStr > endStr) {
      showAlert?.("Start date must be on or before end date.");
      return;
    }
    // Pause: start and end within subscription period. Extension: start within period; end can be after plan. Start <= end for both.
    const sub = subscriptions.find((s) => s.id === subId);
    const extType = (formExt.extension_type || "pause").toLowerCase();
    if (sub?.active_from != null && sub?.active_to != null) {
      const subStart = String(sub.active_from).slice(0, 10);
      const subEnd = String(sub.active_to).slice(0, 10);
      if (extType === "pause") {
        if (startStr < subStart || startStr > subEnd) {
          showAlert?.("Pause start date must be within the subscription period (from plan start to plan end).");
          return;
        }
        if (endStr < subStart || endStr > subEnd) {
          showAlert?.("Pause end date must be within the subscription period (from plan start to plan end).");
          return;
        }
      } else if (extType === "extension") {
        if (startStr < subStart || startStr > subEnd) {
          showAlert?.("Extension start date must be within the subscription period. End date can be after the plan.");
          return;
        }
      }
    }
    setSaving(true);
    try {
      const res = await modifyClientSubscriptionExtensions({
        event_type_id: eventTypeId,
        subscription_extensions: [
          {
            operation: "ADD",
            id: null,
            client_subscription_id: subId,
            extension_type: formExt.extension_type || "pause",
            start_date: formExt.start_date,
            end_date: formExt.end_date,
            reason,
          },
        ],
      });
      if (res.success) {
        setAddExtensionSubscriptionId(null);
        const sub = subscriptions.find((s) => s.id === subId);
        const subStart = sub?.active_from ? String(sub.active_from).slice(0, 10) : null;
        const subEnd = sub?.active_to ? String(sub.active_to).slice(0, 10) : null;
        const nextStart = subStart && subEnd ? defaultDateInRange(subStart, subEnd) : today();
        setFormExt({ extension_type: "pause", start_date: nextStart, end_date: nextStart, reason: "" });
        showAlert?.("Pause/extension added");
        try {
          await loadData();
        } catch (refetchErr) {
          console.warn("Refetch after add extension failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to add");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const handleEditExtension = async (e) => {
    e.preventDefault();
    if (!editExtensionId || !addExtensionSubscriptionId) return;
    const subId = addExtensionSubscriptionId;
    const reason = formExt.reason?.trim() || null;
    if (!formExt.start_date || !formExt.end_date) {
      showAlert?.("Please enter start and end dates.");
      return;
    }
    if (!reason) {
      showAlert?.("Please enter a reason.");
      return;
    }
    const startStr = String(formExt.start_date).slice(0, 10);
    const endStr = String(formExt.end_date).slice(0, 10);
    if (startStr > endStr) {
      showAlert?.("Start date must be on or before end date.");
      return;
    }
    const sub = subscriptions.find((s) => s.id === subId);
    const extType = (formExt.extension_type || "pause").toLowerCase();
    if (sub?.active_from != null && sub?.active_to != null) {
      const subStart = String(sub.active_from).slice(0, 10);
      const subEnd = String(sub.active_to).slice(0, 10);
      if (extType === "pause") {
        if (startStr < subStart || startStr > subEnd) {
          showAlert?.("Pause start date must be within the subscription period (from plan start to plan end).");
          return;
        }
        if (endStr < subStart || endStr > subEnd) {
          showAlert?.("Pause end date must be within the subscription period (from plan start to plan end).");
          return;
        }
      } else if (extType === "extension") {
        if (startStr < subStart || startStr > subEnd) {
          showAlert?.("Extension start date must be within the subscription period. End date can be after the plan.");
          return;
        }
      }
    }
    setSaving(true);
    try {
      const res = await modifyClientSubscriptionExtensions({
        event_type_id: eventTypeId,
        subscription_extensions: [
          {
            operation: "UPDATE",
            id: editExtensionId,
            client_subscription_id: subId,
            extension_type: formExt.extension_type || "pause",
            start_date: formExt.start_date,
            end_date: formExt.end_date,
            reason,
          },
        ],
      });
      if (res.success) {
        setEditExtensionId(null);
        setAddExtensionSubscriptionId(null);
        setFormExt({ extension_type: "pause", start_date: "", end_date: "", reason: "" });
        showAlert?.("Pause/extension updated");
        try {
          await loadData();
        } catch (refetchErr) {
          console.warn("Refetch after edit extension failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to update");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubscription = async (id) => {
    if (!window.confirm("Remove this subscription?")) return;
    setSaving(true);
    try {
      const res = await modifyClientSubscriptions({
        event_type_id: eventTypeId,
        subscriptions: [{ operation: "DELETE", id }],
      });
      if (res.success) {
        showAlert?.("Subscription removed");
        try {
          await loadData();
        } catch (refetchErr) {
          console.warn("Refetch after delete subscription failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to remove");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to remove");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCoach = async (id) => {
    if (!window.confirm("Remove this coach?")) return;
    setSaving(true);
    try {
      const res = await modifyClientSubscriptionCoaches({
        event_type_id: eventTypeId,
        subscription_coaches: [{ operation: "DELETE", id }],
      });
      if (res.success) {
        if (id === editCoachId) {
          setEditCoachId(null);
          setFormCoach({ coach_type: "fitness", coach_id: "", active_from: "" });
        }
        showAlert?.("Coach removed");
        try {
          await loadData();
        } catch (refetchErr) {
          console.warn("Refetch after delete coach failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to remove");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to remove");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExtension = async (id) => {
    if (!window.confirm("Remove this?")) return;
    setSaving(true);
    try {
      const res = await modifyClientSubscriptionExtensions({
        event_type_id: eventTypeId,
        subscription_extensions: [{ operation: "DELETE", id }],
      });
      if (res.success) {
        if (id === editExtensionId) {
          setEditExtensionId(null);
          setAddExtensionSubscriptionId(null);
          setFormExt({ extension_type: "pause", start_date: "", end_date: "", reason: "" });
        }
        showAlert?.("Removed");
        try {
          await loadData();
        } catch (refetchErr) {
          console.warn("Refetch after delete extension failed:", refetchErr);
        }
      } else {
        showAlert?.(res.message || "Failed to remove");
      }
    } catch (err) {
      showAlert?.(err?.message || "Failed to remove");
    } finally {
      setSaving(false);
    }
  };

  const openModifyCoaches = (subscriptionId) => {
    setEditCoachId(null);
    setFormCoach({ coach_type: "fitness", coach_id: "", active_from: "" });
    setModifyCoachSubscriptionId(subscriptionId);
  };

  const openEditCoach = (c) => {
    const fromDate = c.active_from ? String(c.active_from).slice(0, 10) : "";
    setFormCoach({
      coach_type: (c.coach_type || "fitness").toLowerCase(),
      coach_id: c.coach_id || "",
      active_from: fromDate,
    });
    setEditCoachId(c.id);
  };

  const cancelEditCoach = () => {
    setEditCoachId(null);
    setFormCoach({ coach_type: "fitness", coach_id: "", active_from: "" });
  };

  const openAddExtension = (subscriptionId) => {
    setEditExtensionId(null);
    setFormExt({ extension_type: "pause", start_date: "", end_date: "", reason: "" });
    setAddExtensionSubscriptionId(subscriptionId);
  };

  const openEditExtension = (ex) => {
    setAddExtensionSubscriptionId(ex.client_subscription_id);
    setEditExtensionId(ex.id);
    setFormExt({
      extension_type: ex.extension_type || "pause",
      start_date: ex.start_date ? String(ex.start_date).slice(0, 10) : "",
      end_date: ex.end_date ? String(ex.end_date).slice(0, 10) : "",
      reason: ex.reason || "",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[280px]">
        <Loader />
      </div>
    );
  }

  const planOptions = plans.map((p) => ({
    planId: p.planId,
    planName: p.planName,
    planDetails: p.planDetails || [],
    requiredEmployees: p.requiredEmployees,
    pricingCount: (p.pricing || []).length,
    countries: Array.from(new Set((p.pricing || []).map((pr) => pr.country).filter(Boolean))),
    currencies: Array.from(new Set((p.pricing || []).map((pr) => pr.currency).filter(Boolean))),
    durations: Array.from(new Set((p.pricing || []).map((pr) => pr.duration).filter((d) => d != null))),
  }));
  const filteredPlanOptions = planOptions.filter((plan) => {
    const query = planCatalogQuery.trim().toLowerCase();
    if (!query) return true;
    const detailText = (plan.planDetails || []).join(" ").toLowerCase();
    return (
      plan.planName.toLowerCase().includes(query) ||
      detailText.includes(query)
    );
  });
  const selectedPricingMeta = flatPricings.find(
    (p) => String(p.id) === String(formSub.plan_pricing_id)
  );

  const renderPlanPricingSelectors = () => (
    <>
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Select Plan Pricing
        </p>
        <p className="text-xs text-slate-500 mb-2">
          Expand a plan card and pick a pricing row directly.
        </p>
        <input
          type="text"
          value={planCatalogQuery}
          onChange={(e) => setPlanCatalogQuery(e.target.value)}
          placeholder="Search plans by name or detail..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-orange-primary/40 focus:border-orange-primary bg-white"
        />
        <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
          {filteredPlanOptions.length === 0 ? (
            <p className="text-xs text-slate-500 py-2">No plans found for this search.</p>
          ) : (
            filteredPlanOptions.map((plan) => {
              const isSelectedPlan = String(formSub.selected_plan_id) === String(plan.planId);
              const planPricings = flatPricings.filter((pr) => String(pr.planId) === String(plan.planId));
              return (
                <div
                  key={plan.planId}
                  className={`w-full rounded-lg border p-3 transition-colors ${
                    isSelectedPlan
                      ? "border-orange-300 bg-orange-50"
                      : "border-slate-200 bg-white hover:border-orange-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setFormSub((prev) => ({
                        ...prev,
                        selected_plan_id: String(plan.planId),
                        selected_country: "",
                        selected_currency: "",
                        plan_pricing_id: "",
                        amount: "",
                      }))
                    }
                    className="w-full text-left"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{plan.planName}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{plan.requiredEmployees} users</p>
                      </div>
                      <span className="text-xs font-medium text-slate-500">
                        {plan.pricingCount} pricing option{plan.pricingCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {(plan.planDetails || []).length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {plan.planDetails.slice(0, 2).map((detail, idx) => (
                          <li key={idx} className="text-xs text-slate-600">
                            • {detail}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-2 text-xs text-slate-500">
                      Countries: {plan.countries.slice(0, 3).join(", ") || "—"}
                      {plan.countries.length > 3 ? "..." : ""} | Denominations: {plan.currencies.join(", ") || "—"}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Durations: {plan.durations.length ? plan.durations.map((d) => `${d}m`).join(", ") : "—"}
                    </div>
                  </button>
                  {isSelectedPlan && (
                    <div className="mt-3 rounded-lg border border-orange-200 bg-white overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-orange-50 text-slate-700">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold">Country</th>
                            <th className="text-left px-3 py-2 font-semibold">Denomination</th>
                            <th className="text-left px-3 py-2 font-semibold">Duration</th>
                            <th className="text-left px-3 py-2 font-semibold">Price</th>
                            <th className="text-left px-3 py-2 font-semibold">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {planPricings.map((pricing) => {
                            const isSelectedPricing = String(formSub.plan_pricing_id) === String(pricing.id);
                            return (
                              <tr key={pricing.id} className={isSelectedPricing ? "bg-emerald-50" : "bg-white"}>
                                <td className="px-3 py-2 border-t border-slate-100">{pricing.country || "—"}</td>
                                <td className="px-3 py-2 border-t border-slate-100">{pricing.currency || "—"}</td>
                                <td className="px-3 py-2 border-t border-slate-100">
                                  {pricing.durationMonths != null ? `${pricing.durationMonths} months` : "—"}
                                </td>
                                <td className="px-3 py-2 border-t border-slate-100">{pricing.price != null ? pricing.price : "—"}</td>
                                <td className="px-3 py-2 border-t border-slate-100">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setFormSub((prev) => ({
                                        ...prev,
                                        selected_plan_id: String(plan.planId),
                                        selected_country: pricing.country || "",
                                        selected_currency: pricing.currency || "",
                                        plan_pricing_id: pricing.id,
                                        amount: pricing.price != null ? String(pricing.price) : prev.amount,
                                      }))
                                    }
                                    className={`px-2.5 py-1 rounded-md font-medium ${
                                      isSelectedPricing
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                                    }`}
                                  >
                                    {isSelectedPricing ? "Selected" : "Select"}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {selectedPricingMeta && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">
            Selected Pricing Summary
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-slate-500">Plan</p>
              <p className="font-medium text-slate-800">{selectedPricingMeta.planName}</p>
            </div>
            <div>
              <p className="text-slate-500">Duration</p>
              <p className="font-medium text-slate-800">{selectedPricingMeta.durationMonths != null ? `${selectedPricingMeta.durationMonths} months` : "—"}</p>
            </div>
            <div>
              <p className="text-slate-500">Country</p>
              <p className="font-medium text-slate-800">{selectedPricingMeta.country || "—"}</p>
            </div>
            <div>
              <p className="text-slate-500">Denomination</p>
              <p className="font-medium text-slate-800">{selectedPricingMeta.currency || "—"}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // No subscription: show only "Add subscription"
  if (subscriptions.length === 0) {
    return (
      <div className="w-full max-w-full min-w-0 flex flex-col items-center justify-center py-20 px-6">
        <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100/80 border border-slate-200/60 p-8 mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <CreditCard className="w-14 h-14 text-slate-400" strokeWidth={1.25} />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 tracking-tight">No subscription yet</h2>
        <p className="text-sm text-slate-500 mt-2 text-center max-w-sm">
          Add a plan for this user to manage coaches and pauses.
        </p>
        <button
          type="button"
          onClick={() => setAddSubscriptionOpen(true)}
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-[0_2px_8px_rgba(249,115,22,0.35)] hover:from-orange-600 hover:to-orange-700 hover:shadow-[0_4px_12px_rgba(249,115,22,0.4)] transition-all"
        >
          <Plus className="w-5 h-5" strokeWidth={2} />
          Add subscription
        </button>

        {addSubscriptionOpen && (
          <Modal
            title="Add subscription"
            onClose={() => setAddSubscriptionOpen(false)}
            maxWidthClass="max-w-5xl"
          >
            <form onSubmit={handleAddSubscription} className="space-y-4">
              {renderPlanPricingSelectors()}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Amount <span className="text-red-500">*</span></label>
                <p className="text-xs text-gray-500 mb-1">Filled from selected pricing option; change if needed.</p>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formSub.amount}
                  onChange={(e) => setFormSub((s) => ({ ...s, amount: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary"
                  required
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Start date <span className="text-red-500">*</span></label>
                <DateInput value={formSub.active_from} onChange={(v) => setFormSub((s) => ({ ...s, active_from: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment date <span className="text-red-500">*</span></label>
                <p className="text-xs text-gray-500 mb-1">When payment was received.</p>
                <DateInput value={formSub.payment_date} onChange={(v) => setFormSub((s) => ({ ...s, payment_date: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sales rep</label>
                <select
                  value={formSub.sales_rep_employee_id}
                  onChange={(e) => setFormSub((s) => ({ ...s, sales_rep_employee_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary"
                >
                  <option value="">Select sales rep (optional)</option>
                  {employees.map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {[emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.employee_id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saving} text={saving ? "Saving…" : "Save"} className="bg-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" />
                <button type="button" onClick={() => setAddSubscriptionOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">Cancel</button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    );
  }

  // Has subscription(s): show one card per subscription with details and contextual actions — full width
  return (
    <div className="w-full max-w-full min-w-0 space-y-6">
      {subscriptions.map((sub) => {
        const subCoaches = coachesBySubId[sub.id] || [];
        const subExtensions = extensionsBySubId[sub.id] || [];
        const planLabel = subscriptionLabelById[sub.id] || pricingLabelById[sub.plan_pricing_id];

        return (
          <div key={sub.id} className="w-full max-w-full group relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-slate-200/80 overflow-hidden transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:border-slate-200">
            {/* Accent bar — slightly wider for presence */}
            <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-orange-400 to-orange-600 rounded-l-2xl" />

            <div className="pl-7 pr-8 py-7 min-w-0">
              {/* Actions */}
              <div className="flex justify-end gap-1 -mt-1 mb-5">
                <button
                  type="button"
                  onClick={() => openEditSubscription(sub)}
                  disabled={saving}
                  className="p-3 rounded-xl text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-50"
                  title="Edit subscription"
                >
                  <Pencil className="w-4 h-4" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteSubscription(sub.id)}
                  disabled={saving}
                  className="p-3 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
                  title="Remove subscription"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>

              {/* Date — top, comfortable size */}
              <div className="flex items-center gap-2.5 text-base text-slate-600 mb-2">
                <Calendar className="w-5 h-5 text-slate-400 shrink-0" />
                <span>{formatDate(sub.active_from)} → {sub.active_to ? formatDate(sub.active_to) : "—"}</span>
              </div>

              {/* Plan name — main focus */}
              <h2 className="text-xl font-semibold text-slate-900 tracking-tight mb-6">{planLabel}</h2>

              {/* Amount + Sales rep — padded block, easy to scan */}
              <div className="rounded-xl bg-slate-50/90 border border-slate-100 py-4 px-5 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-slate-500 text-sm font-medium w-24 shrink-0">Amount</span>
                  <span className="font-semibold text-slate-900 text-base inline-flex items-center gap-1.5">
                    {sub.amount != null ? (
                      <>
                        <IndianRupee className="w-4 h-4 text-slate-500" />
                        {sub.amount}
                      </>
                    ) : "—"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-500 text-sm font-medium w-24 shrink-0">Sales rep</span>
                  <span className="text-slate-800 text-base">
                    {sub.sales_rep_employee_id ? (employeeIdToName[sub.sales_rep_employee_id] || "—") : "—"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-500 text-sm font-medium w-24 shrink-0">Payment date</span>
                  <span className="text-slate-800 text-base inline-flex items-center gap-1.5">
                    <Banknote className="w-4 h-4 text-slate-500 shrink-0" />
                    {sub.payment_date ? formatDate(sub.payment_date) : "—"}
                  </span>
                </div>
              </div>

              {/* Coaches & Extensions — more space */}
              <div className="mt-8 pt-6 border-t border-slate-200/80 space-y-6">
                {/* Coaches */}
                <div className="rounded-xl bg-slate-50/90 border border-slate-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      Coaches
                    </h3>
                    <button
                      type="button"
                      onClick={() => openModifyCoaches(sub.id)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                      Modify
                    </button>
                  </div>
                  {subCoaches.length === 0 ? (
                    <p className="text-sm text-slate-500 py-1">No coaches assigned.</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {subCoaches.map((c) => (
                        <li key={c.id} className="flex items-center justify-between gap-3 py-3 px-4 rounded-lg bg-white border border-slate-100 shadow-sm/50">
                          <span className="text-sm text-slate-700">
                            {c.coach_type ? `${String(c.coach_type).charAt(0).toUpperCase() + String(c.coach_type).slice(1)}: ` : ""}
                            {employeeIdToName[c.coach_id] || c.coach_id?.slice(0, 8) || "—"}
                          </span>
                          <span className="text-slate-500 text-sm">from {formatDate(c.active_from)}</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteCoach(c.id)}
                            disabled={saving}
                            className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50 transition-colors"
                            title="Remove coach"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Pauses & extensions */}
                <div className="rounded-xl bg-slate-50/90 border border-slate-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                      <CalendarClock className="w-4 h-4 text-slate-400" />
                      Pauses & extensions
                    </h3>
                    <button
                      type="button"
                      onClick={() => openAddExtension(sub.id)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                  {subExtensions.length === 0 ? (
                    <p className="text-sm text-slate-500 py-1">No pauses or extensions.</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {subExtensions.map((ex) => (
                        <li key={ex.id} className={`flex items-center justify-between gap-3 py-3 px-4 rounded-lg bg-white border shadow-sm/50 ${editExtensionId === ex.id ? "border-orange-300 bg-orange-50/50" : "border-slate-100"}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`shrink-0 inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${ex.extension_type === "pause" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                              {ex.extension_type || "—"}
                            </span>
                            <span className="text-slate-600 text-sm truncate" title={ex.reason}>{ex.reason || "—"}</span>
                          </div>
                          <span className="text-slate-500 text-sm shrink-0">{formatDate(ex.start_date)} – {formatDate(ex.end_date)}</span>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => openEditExtension(ex)}
                              disabled={saving}
                              className="p-2 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 disabled:opacity-50 transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteExtension(ex.id)}
                              disabled={saving}
                              className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50 transition-colors"
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Add another subscription */}
      <div className="flex justify-center pt-2">
        <button
          type="button"
          onClick={() => {
            setFormSub({
              plan_pricing_id: "",
              selected_plan_id: "",
              selected_country: "",
              selected_currency: "",
              amount: "",
              active_from: today(),
              payment_date: today(),
              sales_rep_employee_id: "",
            });
            setAddSubscriptionOpen(true);
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200/80 hover:border-slate-300 transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Add another subscription
        </button>
      </div>

      {/* Add subscription modal (for "Add another") */}
      {addSubscriptionOpen && (
        <Modal
          title="Add subscription"
          onClose={() => setAddSubscriptionOpen(false)}
          maxWidthClass="max-w-5xl"
        >
          <form onSubmit={handleAddSubscription} className="space-y-4">
            {renderPlanPricingSelectors()}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Amount <span className="text-red-500">*</span></label>
              <input type="number" step="0.01" min="0" value={formSub.amount} onChange={(e) => setFormSub((s) => ({ ...s, amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required placeholder="0.00" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Start date <span className="text-red-500">*</span></label>
              <DateInput value={formSub.active_from} onChange={(v) => setFormSub((s) => ({ ...s, active_from: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment date <span className="text-red-500">*</span></label>
              <p className="text-xs text-gray-500 mb-1">When payment was received.</p>
              <DateInput value={formSub.payment_date} onChange={(v) => setFormSub((s) => ({ ...s, payment_date: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Sales rep</label>
              <select
                value={formSub.sales_rep_employee_id}
                onChange={(e) => setFormSub((s) => ({ ...s, sales_rep_employee_id: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary"
              >
                <option value="">Select sales rep (optional)</option>
                {employees.map((emp) => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {[emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.employee_id}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={saving} text={saving ? "Saving…" : "Save"} className="bg-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" />
              <button type="button" onClick={() => setAddSubscriptionOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit subscription modal */}
      {editSubscriptionId && (
        <Modal title="Edit subscription" onClose={() => {
          setEditSubscriptionId(null);
          setFormSub({
            plan_pricing_id: "",
            selected_plan_id: "",
            selected_country: "",
            selected_currency: "",
            amount: "",
            active_from: "",
            payment_date: "",
            sales_rep_employee_id: "",
          });
        }} maxWidthClass="max-w-5xl">
          <form onSubmit={handleEditSubscription} className="space-y-4">
            {renderPlanPricingSelectors()}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Amount <span className="text-red-500">*</span></label>
              <input type="number" step="0.01" min="0" value={formSub.amount} onChange={(e) => setFormSub((s) => ({ ...s, amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required placeholder="0.00" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Start date <span className="text-red-500">*</span></label>
              <DateInput value={formSub.active_from} onChange={(v) => setFormSub((s) => ({ ...s, active_from: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment date <span className="text-red-500">*</span></label>
              <p className="text-xs text-gray-500 mb-1">When payment was received.</p>
              <DateInput value={formSub.payment_date} onChange={(v) => setFormSub((s) => ({ ...s, payment_date: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Sales rep</label>
              <select
                value={formSub.sales_rep_employee_id}
                onChange={(e) => setFormSub((s) => ({ ...s, sales_rep_employee_id: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary"
              >
                <option value="">Select sales rep (optional)</option>
                {employees.map((emp) => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {[emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.employee_id}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={saving} text={saving ? "Saving…" : "Save"} className="bg-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" />
              <button type="button" onClick={() => {
                setEditSubscriptionId(null);
                setFormSub({
                  plan_pricing_id: "",
                  selected_plan_id: "",
                  selected_country: "",
                  selected_currency: "",
                  amount: "",
                  active_from: "",
                  payment_date: "",
                  sales_rep_employee_id: "",
                });
              }} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modify coaches modal */}
      {modifyCoachSubscriptionId && (
        <Modal
          title={`Coaches – ${subscriptionLabelById[modifyCoachSubscriptionId] || "Subscription"}`}
          onClose={() => { setModifyCoachSubscriptionId(null); setEditCoachId(null); }}
        >
          <div className="space-y-5">
            {/* Current coaches – premium cards with Edit & Delete */}
            {(coachesBySubId[modifyCoachSubscriptionId] || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Current coaches</p>
                <ul className="space-y-2">
                  {(coachesBySubId[modifyCoachSubscriptionId] || []).map((c) => (
                    <li
                      key={c.id}
                      className={`flex items-center justify-between gap-3 py-3 px-4 rounded-xl border transition-all ${
                        editCoachId === c.id
                          ? "bg-orange-50/80 border-orange-200 ring-1 ring-orange-200"
                          : "bg-slate-50/90 border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="min-w-0">
                        <span className="font-medium text-slate-800">
                          {c.coach_type ? `${String(c.coach_type).charAt(0).toUpperCase() + String(c.coach_type).slice(1)}: ` : ""}
                          {employeeIdToName[c.coach_id] || c.coach_id?.slice(0, 8) || "—"}
                        </span>
                        <span className="text-slate-500 text-sm ml-1.5">from {formatDate(c.active_from)}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => openEditCoach(c)}
                          disabled={saving}
                          className="p-2 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 disabled:opacity-50 transition-colors"
                          title="Edit coach"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCoach(c.id)}
                          disabled={saving}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                          title="Remove coach"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Edit coach form – when a coach is being edited */}
            {editCoachId ? (
              <div className="rounded-xl bg-orange-50/80 border border-orange-200 p-4 space-y-4">
                <p className="text-sm font-semibold text-slate-700">Edit coach</p>
                <form onSubmit={handleEditCoach} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Coach type <span className="text-red-500">*</span></label>
                    <select
                      value={formCoach.coach_type}
                      onChange={(e) => setFormCoach((s) => ({ ...s, coach_type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary bg-white"
                      required
                    >
                      <option value="fitness">Fitness</option>
                      <option value="diet">Diet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Coach <span className="text-red-500">*</span></label>
                    <select
                      value={formCoach.coach_id}
                      onChange={(e) => setFormCoach((s) => ({ ...s, coach_id: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary bg-white"
                      required
                    >
                      <option value="">Select coach</option>
                      {(employees || []).map((emp) => (
                        <option key={emp.employee_id} value={emp.employee_id}>
                          {[emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.employee_id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Active from <span className="text-red-500">*</span></label>
                    <DateInput value={formCoach.active_from} onChange={(v) => setFormCoach((s) => ({ ...s, active_from: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary bg-white" required />
                    {modifyCoachSubscriptionId && (() => {
                      const sub = subscriptions.find((s) => s.id === modifyCoachSubscriptionId);
                      if (sub?.active_from && sub?.active_to) return (
                        <p className="mt-1 text-xs text-slate-500">Must be within subscription period: {formatDate(sub.active_from)} – {formatDate(sub.active_to)}</p>
                      );
                      return null;
                    })()}
                  </div>
                  <div className="flex gap-3 pt-1">
                    <Button type="submit" disabled={saving} text={saving ? "Saving…" : "Save changes"} className="bg-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" />
                    <button type="button" onClick={cancelEditCoach} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors">Cancel</button>
                  </div>
                </form>
              </div>
            ) : (
              /* Add coach form */
              <form onSubmit={handleAddCoach} className="pt-2 border-t border-slate-200 space-y-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Add coach</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Coach type <span className="text-red-500">*</span></label>
                  <select
                    value={formCoach.coach_type}
                    onChange={(e) => setFormCoach((s) => ({ ...s, coach_type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary"
                    required
                  >
                    <option value="fitness">Fitness</option>
                    <option value="diet">Diet</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Coach <span className="text-red-500">*</span></label>
                  <select
                    value={formCoach.coach_id}
                    onChange={(e) => setFormCoach((s) => ({ ...s, coach_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary"
                    required
                  >
                    <option value="">Select coach</option>
                    {(employees || []).map((emp) => (
                      <option key={emp.employee_id} value={emp.employee_id}>
                        {[emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.employee_id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Active from <span className="text-red-500">*</span></label>
                  <DateInput value={formCoach.active_from} onChange={(v) => setFormCoach((s) => ({ ...s, active_from: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
                  {modifyCoachSubscriptionId && (() => {
                    const sub = subscriptions.find((s) => s.id === modifyCoachSubscriptionId);
                    if (sub?.active_from && sub?.active_to) return (
                      <p className="mt-1 text-xs text-slate-500">Must be within subscription period: {formatDate(sub.active_from)} – {formatDate(sub.active_to)}</p>
                    );
                    return null;
                  })()}
                </div>
                <div className="flex gap-3">
                  <Button type="submit" disabled={saving} text={saving ? "Saving…" : "Assign"} className="bg-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" />
                  <button type="button" onClick={() => setModifyCoachSubscriptionId(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">Done</button>
                </div>
              </form>
            )}
          </div>
        </Modal>
      )}

      {/* Add / Edit pause or extension modal */}
      {addExtensionSubscriptionId && (
        <Modal
          title={editExtensionId ? "Edit pause or extension" : `Add pause or extension – ${subscriptionLabelById[addExtensionSubscriptionId] || "Subscription"}`}
          onClose={() => { setAddExtensionSubscriptionId(null); setEditExtensionId(null); setFormExt({ extension_type: "pause", start_date: "", end_date: "", reason: "" }); }}
        >
          <form onSubmit={editExtensionId ? handleEditExtension : handleAddExtension} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Type <span className="text-red-500">*</span></label>
              <select value={formExt.extension_type} onChange={(e) => setFormExt((s) => ({ ...s, extension_type: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required>
                <option value="pause">Pause</option>
                <option value="extension">Extension</option>
              </select>
            </div>
            {addExtensionSubscriptionId && (() => {
              const sub = subscriptions.find((s) => s.id === addExtensionSubscriptionId);
              const isPause = (formExt.extension_type || "pause").toLowerCase() === "pause";
              if (!sub?.active_from || !sub?.active_to) return null;
              if (isPause) {
                return (
                  <p className="text-xs text-slate-500">Pause start and end must be within subscription period: {formatDate(sub.active_from)} – {formatDate(sub.active_to)}</p>
                );
              }
              return (
                <p className="text-xs text-slate-500">Extension start must be within period: {formatDate(sub.active_from)} – {formatDate(sub.active_to)}. End date can be after the plan. Start on or before end date.</p>
              );
            })()}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Start date <span className="text-red-500">*</span></label>
                <DateInput value={formExt.start_date} onChange={(v) => setFormExt((s) => ({ ...s, start_date: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">End date <span className="text-red-500">*</span></label>
                <DateInput value={formExt.end_date} onChange={(v) => setFormExt((s) => ({ ...s, end_date: v }))} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Reason <span className="text-red-500">*</span></label>
              <input type="text" value={formExt.reason} onChange={(e) => setFormExt((s) => ({ ...s, reason: e.target.value }))} placeholder="e.g. Medical leave, Travel" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary" required />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={saving} text={saving ? "Saving…" : (editExtensionId ? "Save changes" : "Save")} className="bg-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" />
              <button type="button" onClick={() => { setAddExtensionSubscriptionId(null); setEditExtensionId(null); setFormExt({ extension_type: "pause", start_date: "", end_date: "", reason: "" }); }} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
